import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cx } from "@/components/FormFields";

const Kpi = ({ label, value, accent }) => (
  <div className="rounded-xl border border-[#232f25] bg-[#111712] p-5">
    <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#69776b]">{label}</div>
    <div className={cx("font-display text-3xl font-black mt-2", accent ? "text-[#8CE63C]" : "text-white")}>{value}</div>
  </div>
);

const badgeFor = (s) => ({
  paid: "bg-[#132717] border-[#8CE63C] text-[#8CE63C]",
  pending: "bg-[#2A240E] border-[#FACC15] text-[#FDE047]",
  failed: "bg-[#261D1D] border-[#F87171] text-[#FCA5A5]",
}[s] || "bg-[#0d120e] border-[#232f25] text-[#9EABA0]");

export function PaymentsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/payments/admin").then((r) => setData(r.data)).catch(() => toast.error("Failed to load payments.")).finally(() => setLoading(false));
  }, []);

  const orders = data?.orders || [];
  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div>
      {data?.mode === "test" && data?.onboarding_url && (
        <div className="rounded-xl border border-[#8CE63C]/40 bg-[#111712] p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-display text-lg font-bold text-white tracking-tight">Connect Your Stripe Account</h3>
            <p className="text-xs text-[#9EABA0] mt-1 max-w-xl leading-relaxed">
              Payments are running in <span className="text-[#FDE047] font-semibold">TEST mode</span>. Claim your Stripe account to start accepting real card payments — your live keys are connected automatically once approved.
            </p>
          </div>
          <a href={data.onboarding_url} target="_blank" rel="noreferrer" data-testid="stripe-connect-link" className="shrink-0 px-5 py-2.5 rounded-md bg-[#8CE63C] text-[#080b08] text-sm font-bold uppercase tracking-wide hover:bg-[#9df04f] transition-colors text-center">
            Claim / Connect Stripe
          </a>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <Kpi label="Paid Orders" value={data?.paid_count ?? "—"} accent />
        <Kpi label="Revenue (paid)" value={data ? `$${(data.revenue || 0).toLocaleString()}` : "—"} />
        <Kpi label="Mode" value={(data?.mode || "test").toUpperCase()} />
      </div>

      <div className="rounded-xl border border-[#232f25] bg-[#111712] overflow-hidden">
        <div className="overflow-x-auto">
          <table data-testid="admin-payments-table" className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#232f25] text-left">
                {["Date", "Product", "Qty", "Amount", "Status", "Session"].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-[#69776b] whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-[#69776b]">Loading…</td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-[#69776b]">No orders yet.</td></tr>
              ) : (
                orders.map((o) => (
                  <tr key={o.session_id} className="border-b border-[#1a211b] hover:bg-[#0d120e] transition-colors">
                    <td className="px-4 py-3 text-[#9EABA0] whitespace-nowrap">{fmt(o.created_at)}</td>
                    <td className="px-4 py-3 text-white font-medium">{o.product_name}</td>
                    <td className="px-4 py-3 text-[#9EABA0]">{o.quantity}</td>
                    <td className="px-4 py-3 text-[#8CE63C] font-bold">${o.amount}</td>
                    <td className="px-4 py-3"><span className={cx("px-2 py-1 rounded text-[10px] font-semibold border", badgeFor(o.payment_status))}>{o.payment_status}</span></td>
                    <td className="px-4 py-3 font-mono text-[10px] text-[#69776b]">{(o.session_id || "").slice(0, 18)}…</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
